import type { UserSchema } from '@klear/ui/lib/user-form-schema'
import type { UserRoleName } from '@klear/user/user-role'
import type { UserCreateWire, UserUpdateWire } from '@klear/user/user.wire'
import toNumber from 'lodash/toNumber'

function trimmed(value: string | null | undefined): string | undefined {
  return value?.trim() || undefined
}

export function userSchemaToCreateWire(schema: UserSchema): UserCreateWire {
  const role = schema.roles?.[0]
  if (!role?.role || role.traderId == null) {
    throw new Error('A role and trader are required to create a user')
  }
  const wire: UserCreateWire = {
    email: schema.email.trim().toLowerCase(),
    firstName: schema.firstName.trim(),
    lastName: schema.lastName.trim(),
    jobTitle: schema.jobTitle.trim(),
    role: role.role as UserRoleName,
    traderId: toNumber(role.traderId)
  }
  const middleName = trimmed(schema.middleName)
  if (middleName !== undefined) wire.middleName = middleName
  const phoneNumber = trimmed(schema.phoneNumber)
  if (phoneNumber !== undefined) wire.phoneNumber = phoneNumber
  return wire
}

export function userSchemaToUpdateWire(schema: UserSchema): UserUpdateWire {
  const wire: UserUpdateWire = {
    firstName: schema.firstName.trim(),
    lastName: schema.lastName.trim(),
    jobTitle: schema.jobTitle.trim(),
    middleName: trimmed(schema.middleName) ?? null,
    phoneNumber: trimmed(schema.phoneNumber) ?? null
  }
  const role = schema.roles?.[0]
  if (role?.role && role.traderId != null) {
    wire.role = role.role as UserRoleName
    wire.traderId = toNumber(role.traderId)
  }
  return wire
}

type UserFormSource = {
  id?: number
  email: string
  firstName: string
  middleName?: string | null
  lastName: string
  phoneNumber?: string | null
  jobTitle: string
  roles: ReadonlyArray<{ role: string; traderId: number }>
}

export function userToFormSchema(source: UserFormSource): UserSchema {
  return {
    id: source.id,
    email: source.email,
    firstName: source.firstName,
    middleName: source.middleName ?? '',
    lastName: source.lastName,
    phoneNumber: source.phoneNumber ?? null,
    jobTitle: source.jobTitle,
    roles: source.roles.map((r) => ({ role: r.role, traderId: r.traderId }))
  }
}

export function isKlearRoleName(role: string | undefined | null): boolean {
  return role != null && role.startsWith('klear_')
}

export function isKlearRoleWire(wire: { roles: ReadonlyArray<{ role: string }> }): boolean {
  return wire.roles.some((r) => isKlearRoleName(r.role))
}
